/**
 * ISSUE-1364. The officer's Time out dialog, after v1's `timeout_sheet.dart`:
 * pick how long, add an optional reason only the member sees, confirm.
 * `/chat/member-timeout-set` decides who may do this; this dialog only asks.
 */
import { useChatMemberTimeoutSet } from "@sportsmet/messaging-sdk/private";
import {
  Button,
  confirmDestructive,
  Dialog,
  Input,
  Radio,
} from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import { showApiError, showOfflineError } from "../../errors/apiErrorBus";
import {
  DEFAULT_TIMEOUT_DURATION,
  TIMEOUT_ACTION_LABEL,
  TIMEOUT_DIALOG_BODY,
  TIMEOUT_DURATION_LABEL,
  TIMEOUT_DURATIONS,
  TIMEOUT_REASON_HINT,
  TIMEOUT_REASON_LABEL,
  TIMEOUT_REASON_MAX_LENGTH,
  timeoutDialogTitle,
  type typeTimeoutDuration,
} from "./memberTimeoutLogic";

export function MemberTimeoutDialog({
  chatId,
  target,
  onClose,
}: {
  chatId: string;
  /** The member being timed out; null closes the dialog. */
  target: { userId: string; displayName: string } | null;
  onClose: () => void;
}): ReactNode {
  const timeoutSet = useChatMemberTimeoutSet();
  const [duration, setDuration] = useState<typeTimeoutDuration>(
    DEFAULT_TIMEOUT_DURATION,
  );
  const [reason, setReason] = useState("");

  const close = (): void => {
    setDuration(DEFAULT_TIMEOUT_DURATION);
    setReason("");
    onClose();
  };

  // A typed reason is not thrown away by a stray scrim click or Escape.
  const requestClose = async (): Promise<void> => {
    const isConfirmed = await confirmDestructive({
      title: "Discard this timeout?",
      body: "The reason you typed will be lost.",
      confirmLabel: "Discard",
    });
    if (isConfirmed) close();
  };

  const submit = async (): Promise<void> => {
    if (target === null) return;
    const trimmed = reason.trim();
    try {
      const result = await timeoutSet.mutateAsync({
        chatId,
        userId: target.userId,
        duration,
        ...(trimmed === "" ? {} : { reason: trimmed }),
      });
      if (showApiError(result)) return;
      close();
    } catch {
      showOfflineError();
    }
  };

  return (
    <Dialog
      open={target !== null}
      onOpenChange={(next) => {
        if (!next) close();
      }}
      title={timeoutDialogTitle(target?.displayName ?? "")}
      description={TIMEOUT_DIALOG_BODY}
      autoFocus={false}
      isDirty={reason.trim() !== ""}
      onRequestClose={() => {
        void requestClose();
      }}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => {
              close();
            }}
          >
            Cancel
          </Button>
          <Button
            data-testid="member-timeout-submit"
            loading={timeoutSet.isPending}
            onClick={() => {
              void submit();
            }}
          >
            {TIMEOUT_ACTION_LABEL}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Radio
          label={TIMEOUT_DURATION_LABEL}
          items={TIMEOUT_DURATIONS}
          value={duration}
          onValueChange={(next) => {
            setDuration(next as typeTimeoutDuration);
          }}
        />
        <Input
          label={TIMEOUT_REASON_LABEL}
          hint={TIMEOUT_REASON_HINT}
          maxLength={TIMEOUT_REASON_MAX_LENGTH}
          data-testid="member-timeout-reason"
          value={reason}
          onChange={(event) => {
            setReason(event.target.value);
          }}
        />
      </div>
    </Dialog>
  );
}
